import { ReactNode } from 'react';
import DefinitionTooltip from 'src/components/Tooltip/DefinitionTooltip/DefinitionTooltip';
import { DefinitionTooltipID } from 'src/components/Tooltip/DefinitionTooltip/definitionTooltipConfig';
import { WithChildren, WithClassName } from 'src/utils/UtilityTypes';
import { cm } from 'src/utils/classNames';

function Container({ className, children }: WithChildren<WithClassName>) {
	return (
		<div
			className={cm(
				'flex flex-col gap-y-md rounded-sm border border-stroke-divider p-md',
				'sm:flex-row sm:gap-x-2xl sm:p-2xl',
				className
			)}
		>
			{children}
		</div>
	);
}

interface ItemProps extends WithChildren, WithClassName {
	label: ReactNode;
	tooltipDefinitionId?: DefinitionTooltipID;
}

function Item({ className, children, label, tooltipDefinitionId }: ItemProps) {
	return (
		<div
			className={cm(
				'flex items-center justify-between gap-x-2xl sm:flex-col sm:items-start sm:gap-y-xs',
				className
			)}
		>
			<DefinitionTooltip
				className="typo-body-xs text-text-secondary"
				definitionId={tooltipDefinitionId}
			>
				{label}
			</DefinitionTooltip>
			<div className="typo-label-xs text-text-default sm:typo-heading-h5">
				{children}
			</div>
		</div>
	);
}

/**
 * A single item card that displays a label and a value. only for desktop.
 */
function SingleItemCard({ className, children, label }: ItemProps) {
	return (
		<Container
			className={cm('flex items-center justify-between sm:p-md', className)}
		>
			<div className="typo-body-md text-text-secondary">{label}</div>
			<div className="typo-label-md text-text-default">{children}</div>
		</Container>
	);
}

interface LeftAlignedColCardProps extends WithClassName {
	items: ItemProps[];
}

/**
 * A card that displays a list of items in a left-aligned column.
 * Only for mobile. No Odd Number of Items.
 */
function LeftAlignedColCard({ className, items }: LeftAlignedColCardProps) {
	const pairs = [];
	for (let i = 0; i < items.length; i += 2) {
		pairs.push(items.slice(i, i + 2));
	}

	return (
		<Container className={className}>
			{pairs.map((pair, index) => (
				<div
					key={index}
					className="flex w-full flex-row justify-between gap-x-3xl"
				>
					<Item
						className={cm(
							'w-[50%] justify-start gap-0 [&>*:first-child]:w-[46%]',
							pair[0].className
						)}
						label={pair[0].label}
					>
						{pair[0].children}
					</Item>
					{pair[1] && (
						<Item
							className={cm(
								'w-[50%] justify-start gap-0 [&>*:first-child]:w-[46%]',
								pair[1].className
							)}
							label={pair[1].label}
						>
							{pair[1].children}
						</Item>
					)}
				</div>
			))}
		</Container>
	);
}

export default {
	Container,
	Item,
	SingleItemCard,
	LeftAlignedColCard,
};
